import { noticeError, AFFILIATE_QUERY_PARAM_KEYS, addSearchParams } from '@ratehub/base-ui';
import { fetchCreditCards, fetchCreditCardsFeatured } from '@ratehub/cc-common';

import { Products } from '../definitions/Products';


async function enrichCreditCardProducts(slots, options = {}) {
    // Error most likely unneeded, but just in case script missed a v1 -> v2 migration
    if (!slots.every(product => product.requested)) {
        noticeError(
            new Error('[enrichCreditCardProducts] Product shape is incompatible'),
            { slots },
        );
        return;
    }

    const { hideCTAIfNotMonetized } = options;

    let response = [];
    if (hideCTAIfNotMonetized) {
        response = (await fetchCreditCards({
            ids: slots.map(({ requested }) => requested[0].id),
            ...options,
        })).creditCards;
    } else {
        response = (await fetchCreditCardsFeatured({
            products: slots.map(({ requested, fallback }) => ({
                requested: requested.map(product => ({
                    id: product.id,
                })),
                // BE doesn't fallback correctly if type is undefined/null
                fallback: fallback?.type
                    ? { type: fallback.type }
                    : {},
            })),
            ...options,
        }));
    }

    slots.forEach((slot, index) => {
        let matchedCard;

        for (let i = 0; i < slot.requested.length; i++) {
            const creditCard = slot.requested[i];
            // search the CreditCardShape for the CMS-requested creditCard.id
            matchedCard = response.find(card => creditCard.id === card.id);

            // Cards are not always featured, so a matched card is not guaranteed
            if (matchedCard) {
                enrichCreditCard(creditCard, matchedCard, slot);
                // We only need to enrich the first product we come across.
                break;
            }
        }

        // If no card is found, and hideCTAIfNotMonetized is disabled use fallback
        if (!matchedCard) {
            if (!hideCTAIfNotMonetized) {
                const fallbackCard = response[index];
                if (fallbackCard) {
                    enrichCreditCard(slot.fallback, fallbackCard, slot);
                }
            // otherwise, disable the slot because we won’t replace it with a fallbackCard when hideCTAIfNotMonetized is true
            } else {
                // since we can’t actually delete a slot due to the way slots are passed by reference, we
                // have to mutate slot type and productType to undefined so it won’t show previously fetched product
                Object.assign(slot, { productType: undefined, type: undefined });
            }
        }
    });
}

function enrichCreditCard(cmsEntry, card, slot) {
    if (!cmsEntry || !card) {
        const emptyError = new RangeError(`[enrichCreditCardProducts:enrichCreditCard] ${!cmsEntry
            ? '• missing cmsEntry'
            : ''} ${!card
            ? '• missing card'
            : ''}
        `);
        noticeError(emptyError, {
            cmsEntry,
            card,
        });

        throw emptyError;
    }

    const cmsId = cmsEntry.id?.match(/^\d+$/) // cmsEntry if using fallback may not have an id
        ? parseInt(cmsEntry.id)
        : cmsEntry.id;

    // all enrich* functions are designed to mutate the passed-in object (to enrich them with additional information)
    // this means we MUST mutate the slot object, and cannot just return a new one. The passed-in object is also reused
    // for refetching, so cannot remove or wrongfully update any fields required for fetching.
    Object.assign(slot, {
        id: cmsId || card.id,       // card.id does not require conversion
        productType: Products.CREDIT_CARDS,
        title: cmsEntry.title || card.name,
        isSponsored: cmsEntry.isSponsored || card.isSponsored,      // does not have to prefer cmsEntry
        hasPromo: !!card.giftCardOffer,

        applyHref: getApplyHref(cmsEntry, card),
        // avoid assigning an empty applyText string
        applyText: cmsEntry.applyText?.trim().length
            ? cmsEntry.applyText
            : undefined,
        description: cmsEntry.description || card.description,

        imageSrc: card.cardImageURL,
        imageAlt: card.name,

        // creditCardShape required to support credit card gift offer modal
        product: card,

        // required by heap tracking
        detailsHref: card.detailsURL,
        isMonetized: card.isMonetized,
        providerSlug: card.bank,

        // avoid assigning an empty productTypeOverride string
        productTypeOverride: cmsEntry.productTypeOverride?.trim().length
            ? cmsEntry.productTypeOverride
            : undefined,
    });
}

function getApplyHref(cmsEntry, apiEntry) {
    const apiURL = apiEntry.applyRedirectURL;
    const cmsCustomApplySlug = cmsEntry.customApplySlug;

    if (cmsCustomApplySlug && apiURL) {
        return addSearchParams(apiURL, {
            [ AFFILIATE_QUERY_PARAM_KEYS.customApplySlug ]: cmsCustomApplySlug,
        }).toString();
    } else if (apiURL) {
        return apiURL;
    } else {
        throw new Error(`[enrichCreditCardProducts] Product type “${cmsEntry.type}” with id “${cmsEntry.id}” is missing an apply URL: “${JSON.stringify(apiEntry, null, 4)}”`);
    }
}


export default enrichCreditCardProducts;
